import { useCallback, useEffect, useState } from "react";
import { FiShield } from "react-icons/fi";
import Button from "../../../components/Button/Button";
import FormInput from "../../../components/Form/FormInput";
import api from "../../../utils/axiosInstance";
import { toast } from "../../../components/Toast/toast";

const RecoveryCodes = ({ codes, onDone }) => (
  <div className="mb-4 rounded-lg border border-warning-300 bg-warning-50 p-4">
    <h3 className="text-xs font-semibold uppercase tracking-wider text-warning-900">
      Save these recovery codes now
    </h3>
    <p className="mt-1 text-xs text-warning-800">
      Each code works once and gets you in if you lose your authenticator app.
      This is the only time they are shown.
    </p>

    <ul className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-[13px] text-gray-800 sm:grid-cols-3">
      {codes.map((code) => (
        <li key={code}>{code}</li>
      ))}
    </ul>

    <div className="mt-4 flex gap-2">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          navigator.clipboard
            ?.writeText(codes.join("\n"))
            .then(() => toast.success("Recovery codes copied"))
            .catch(() => toast.error("Could not copy to clipboard"));
        }}
      >
        Copy codes
      </Button>
      <Button size="sm" onClick={onDone}>
        I have saved them
      </Button>
    </div>
  </div>
);

const TwoFactorSection = () => {
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState("idle"); 
  const [setupData, setSetupData] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [fields, setFields] = useState({ code: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get("/auth/mfa");
      setStatus(res.data);
    } catch (err) {
      console.error("Failed to load two-factor status", err);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const reset = () => {
    setMode("idle");
    setSetupData(null);
    setFields({ code: "", password: "" });
    setError("");
    setBusy(false);
  };

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const startSetup = () =>
    run(async () => {
      const res = await api.post("/auth/mfa/setup");
      setSetupData(res.data);
      setMode("setup");
    });

  const confirmSetup = () =>
    run(async () => {
      const res = await api.post("/auth/mfa/enable", { code: fields.code });
      setRecoveryCodes(res.data.recoveryCodes);
      reset();
      await loadStatus();
      toast.success(res.data.message);
    });

  const confirmDisable = () =>
    run(async () => {
      const res = await api.post("/auth/mfa/disable", {
        password: fields.password,
        code: fields.code,
      });
      reset();
      await loadStatus();
      toast.success(res.data.message);
    });

  const confirmRegenerate = () =>
    run(async () => {
      const res = await api.post("/auth/mfa/recovery-codes", {
        password: fields.password,
      });
      setRecoveryCodes(res.data.recoveryCodes);
      reset();
      await loadStatus();
      toast.success(res.data.message);
    });

  if (!status) return null;

  return (
    <div className="rounded-lg border border-gray-100 bg-white p-5 shadow-sm">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold tracking-tight text-gray-900">
            Two-Factor Authentication
          </h2>
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
              status.enabled
                ? "bg-success-100 text-success-800"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            {status.enabled ? "On" : "Off"}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-gray-500">
          {status.enabled
            ? `Logging in asks for a code from your authenticator app. ${status.recoveryCodesRemaining} recovery code${
                status.recoveryCodesRemaining === 1 ? "" : "s"
              } left.`
            : "Add a second step to your login using an authenticator app such as Google Authenticator or Authy."}
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {error}
        </div>
      )}

      {recoveryCodes && (
        <RecoveryCodes codes={recoveryCodes} onDone={() => setRecoveryCodes(null)} />
      )}

      {mode === "idle" && !status.enabled && (
        <Button size="sm" icon={FiShield} onClick={startSetup} disabled={busy}>
          {busy ? "Preparing..." : "Turn on two-factor"}
        </Button>
      )}

      {mode === "idle" && status.enabled && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => setMode("regenerate")}>
            Regenerate recovery codes
          </Button>
          <Button size="sm" variant="dangerSoft" onClick={() => setMode("disable")}>
            Turn off two-factor
          </Button>
        </div>
      )}

      {mode === "setup" && setupData && (
        <div className="grid max-w-2xl grid-cols-1 items-start gap-x-6 gap-y-4 border-t border-gray-100 pt-4 md:grid-cols-2">
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Scan the code
            </h3>
            <img
              src={setupData.qrDataUrl}
              alt="Two-factor setup QR code"
              className="rounded-lg border border-gray-200"
              width={168}
              height={168}
            />
            <p className="mt-2 text-[11px] text-gray-500">
              Can't scan it? Enter this key manually:
              <br />
              <span className="font-mono text-gray-800">{setupData.secret}</span>
            </p>
          </div>

          <div className="space-y-4">
            <FormInput
              label="Verification Code"
              name="code"
              value={fields.code}
              placeholder="123456"
              onChange={(e) => setFields({ ...fields, code: e.target.value })}
            />
            <p className="text-xs text-gray-500">
              Enter the 6-digit code your authenticator app shows to finish.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={confirmSetup} disabled={busy}>
                {busy ? "Verifying..." : "Verify and enable"}
              </Button>
              <Button size="sm" variant="secondary" onClick={reset} disabled={busy}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {mode === "disable" && (
        <div className="max-w-md space-y-4 border-t border-gray-100 pt-4">
          <FormInput
            label="Your Password"
            name="password"
            type="password"
            placeholder="Account password"
            value={fields.password}
            onChange={(e) => setFields({ ...fields, password: e.target.value })}
          />
          <FormInput
            label="Verification Code"
            name="code"
            placeholder="123456"
            value={fields.code}
            onChange={(e) => setFields({ ...fields, code: e.target.value })}
          />

          <div className="flex gap-2">
            <Button size="sm" variant="danger" onClick={confirmDisable} disabled={busy}>
              {busy ? "Turning off..." : "Turn off two-factor"}
            </Button>
            <Button size="sm" variant="secondary" onClick={reset} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {mode === "regenerate" && (
        <div className="max-w-md space-y-4 border-t border-gray-100 pt-4">
          <p className="text-xs text-gray-500">
            Your existing recovery codes stop working once new ones are issued.
          </p>
          <FormInput
            label="Your Password"
            name="password"
            type="password"
            placeholder="Account password"
            value={fields.password}
            onChange={(e) => setFields({ ...fields, password: e.target.value })}
          />

          <div className="flex gap-2">
            <Button size="sm" onClick={confirmRegenerate} disabled={busy}>
              {busy ? "Generating..." : "Generate new codes"}
            </Button>
            <Button size="sm" variant="secondary" onClick={reset} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSection;